import { Box, Button, Flex, Text } from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconFileText,
  IconHome,
  IconKey,
  IconLock,
  IconRefresh,
  IconServer,
} from "@tabler/icons-react";
import { IconProps } from "@tabler/icons-react";
import React from "react";
import { useNavigate } from "react-router-dom";

export type ErrorCode = 404 | 500 | 401 | 403;

interface ErrorStateProps {
  errorCode: ErrorCode;
  title?: string;
  description?: string;
  errorDetails?: string;
  onTryAgain?: () => void;
  onGoBack?: () => void;
  onGoHome?: () => void;
  showActions?: boolean;
  inline?: boolean;
}

const errorConfig: Record<
  ErrorCode,
  {
    icon: React.FC<IconProps>;
    defaultTitle: string;
    defaultDescription: string;
    defaultErrorDetails: string;
  }
> = {
  404: {
    icon: IconFileText,
    defaultTitle: "Not Found",
    defaultDescription:
      "The page or resource you're looking for doesn't exist.",
    defaultErrorDetails: "The requested resource could not be found.",
  },
  500: {
    icon: IconServer,
    defaultTitle: "Server Error",
    defaultDescription:
      "Request failed with status code 500. The server encountered an error.",
    defaultErrorDetails: "Internal server error. Our team has been notified.",
  },
  401: {
    icon: IconKey,
    defaultTitle: "Authentication Required",
    defaultDescription: "Please log in to continue.",
    defaultErrorDetails:
      "You need to be authenticated to access this resource.",
  },
  403: {
    icon: IconLock,
    defaultTitle: "Access Denied",
    defaultDescription: "You don't have permission to access this resource.",
    defaultErrorDetails: "You don't have permission to access this resource.",
  },
};

export const ErrorState: React.FC<ErrorStateProps> = ({
  errorCode,
  title,
  description,
  errorDetails,
  onTryAgain,
  onGoBack,
  onGoHome,
  showActions = true,
  inline = false,
}) => {
  const navigate = useNavigate();
  const config = errorConfig[errorCode];
  const Icon = config.icon;

  const handleTryAgain = () => {
    if (onTryAgain) {
      onTryAgain();
    } else {
      window.location.reload();
    }
  };

  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack();
    } else {
      navigate(-1);
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <Flex
      justify="center"
      align="center"
      direction="column"
      style={
        inline
          ? {
              padding: "40px 20px",
            }
          : {
              minHeight: "calc(100vh - 140px)",
              backgroundColor: "#f8f8f8",
              padding: "40px 20px",
            }
      }
    >
      <Flex
        direction="column"
        align="center"
        justify="center"
        h={450}
        gap="sm"
        style={{
          maxWidth: 600,
          width: "100%",
          padding: "40px 20px",
        }}
      >
        {/* Icon */}
        <Box
          style={{
            backgroundColor: "#fee",
            borderRadius: "12px",
            padding: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={80} color="#d32f2f" />
        </Box>

        {/* Title */}
        <Text weight="bold" size={20} align="center">
          {title || config.defaultTitle}
        </Text>

        {/* Description */}
        <Text
          color="dimmed"
          size={14}
          align="center"
          style={{ maxWidth: "400px", lineHeight: 1.6 }}
        >
          {description || config.defaultDescription}
        </Text>

        {/* Error Code */}
        <Text size={12} align="center">
          Error Code:{" "}
          <Text
            component="span"
            color="red"
            weight={600}
            style={{ fontFamily: '"Geist Mono", monospace' }}
          >
            {errorCode}
          </Text>
        </Text>

        {/* Error Details Section */}
        <Box
          style={{
            width: "100%",
            backgroundColor: "#fff5f5",
            borderRadius: "8px",
            border: "1px solid #ffebee",
            padding: "12px",
            marginTop: "4px",
          }}
        >
          <Flex align="flex-start" gap="sm" mb="xs">
            <IconAlertCircle size={16} color="#d32f2f" />
            <Text weight={600} color="red" size={12}>
              Error Details
            </Text>
          </Flex>
          <Text color="red" size={12} style={{ marginLeft: "24px" }}>
            {errorDetails || config.defaultErrorDetails}
          </Text>
        </Box>

        {/* Action Buttons */}
        {showActions && (
          <Flex
            gap="sm"
            wrap="wrap"
            justify="center"
            style={{ marginTop: "8px" }}
          >
            <Button
              leftIcon={<IconRefresh size={12} />}
              onClick={handleTryAgain}
              size="xs"
              radius="md"
            >
              Try Again
            </Button>
            <Button
              leftIcon={<IconArrowLeft size={12} />}
              onClick={handleGoBack}
              variant="outline"
              size="xs"
              radius="md"
              style={{
                borderColor: "#d0d0d0",
                color: "#333",
              }}
            >
              Go Back
            </Button>
            <Button
              leftIcon={<IconHome size={12} />}
              onClick={handleGoHome}
              variant="outline"
              size="xs"
              radius="md"
              style={{
                borderColor: "#d0d0d0",
                color: "#333",
              }}
            >
              Go Home
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
